/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import Autoplay from 'embla-carousel-autoplay'
import Fade from 'embla-carousel-fade'
import useEmblaCarousel from 'embla-carousel-react'
import Image, { ImageProps } from 'next/image'
import React from 'react'

import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type BackgroundImageAutoplayCarouselProps = {
  /** The duration in milliseconds of the transition between images (default: 5000). */
  transitionDuration?: number

  /** Whether to display a gradient overlay on top of the images (default: true). */
  gradient?: boolean

  /** The images to display in the carousel. */
  images: Array<Pick<ImageProps, 'src' | 'alt' | 'title'>>

  /** The children to display on top of the carousel. */
  children: React.ReactNode
}

/**
 * A carousel that displays images as the background and automatically transitions between them. Designed to be used as
 * a hero image. The height of the carousel is determined by the content inside it, the images are stretched to fill the
 * available space.
 */
const BackgroundImageAutoplayCarousel: React.FC<BackgroundImageAutoplayCarouselProps> = ({
  images,
  transitionDuration = 5000,
  gradient = true,
  children,
}) => {
  const [emblaRef] = useEmblaCarousel({ axis: 'x', loop: true }, [Fade(), Autoplay({ delay: transitionDuration })])

  return (
    <div className={'relative w-full'}>
      <div className={cn('absolute inset-0 overflow-hidden')} ref={emblaRef}>
        <div className={'flex h-full'}>
          {images.map((image, index) => (
            <div className={'relative flex-[0_0_100%] h-full'} key={index}>
              <Image
                fill
                className={'w-full h-full object-cover'}
                src={image.src}
                alt={image.alt}
                title={image.title}
              />
              {gradient && (
                <div
                  className={cn(
                    'absolute inset-0 bg-linear-to-b from-transparent to-black/80',
                    'md:to-black/50 md:bg-linear-to-r',
                  )}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className={'relative py-2'}>{children}</div>
    </div>
  )
}

export { BackgroundImageAutoplayCarousel }
